import _ from 'lodash';
import { combineReducers } from 'redux';
import { format } from 'date-fns';
import { UAParser } from 'ua-parser-js';
import {
  createApiReducer,
  chainReducers,
  INITIAL_API_STATE,
  Status,
} from 'src/api';
import { SIGN_OUT } from 'src/auth';

import {
  FETCH_LOGGED_IN_USER_START,
  FETCH_LOGGED_IN_USER_SUCCESS,
  FETCH_LOGGED_IN_USER_FAILURE,
  IMPERSONATE_ACCOUNT,
  FETCH_MIDS_START,
  FETCH_MIDS_SUCCESS,
  FETCH_MIDS_FAILURE,
  FETCH_LOGIN_HISTORY_START,
  FETCH_LOGIN_HISTORY_SUCCESS,
  FETCH_LOGIN_HISTORY_FAILURE,
} from './actions';

const transformUserFI = response => ({
  id: _.get(response, 'id'),
  businessName: _.get(response, 'financial_institution_business_name'),
  logo: _.get(response, 'financial_institution_logo'),
  logoSmall: _.get(response, 'financial_institution_logo_small'),
  logoBlue: _.get(response, 'financial_institution_logo_blue'),
  logoEmail: _.get(response, 'financial_institution_logo_email'),
  supportName: _.get(response, 'financial_institution_support_name'),
  supportPhone: _.get(response, 'financial_institution_support_phone'),
  allowsBanking: _.get(response, 'financial_institution_allows_banking'),
  allowsPayments: _.get(response, 'financial_institution_allows_payments'),
  allowsLending: _.get(response, 'financial_institution_allows_lending'),
  allowsInsurance: _.get(response, 'financial_institution_allows_insurance'),
  // allowsInsurance: true,
  hasAssignAgents: _.get(
    response,
    'financial_institution_assign_admins_to_accounts'
  ),
  hasAudits: _.get(response, 'financial_institution_has_audits'),
});

const transformUserAccount = (a, response, accountRoles) => {
  const id = _.get(a, 'user_account_account.id');
  const financialInstitutionId = _.get(
    a,
    'user_account_account.account_financial_institution_accounts[0].financial_institution_account_financial_institution',
    null
  );

  // eslint-disable-next-line
  accountRoles[id] = a.user_account_role;
  const financialInstitution = _.find(
    response.user_user_financial_institutions,
    {
      user_financial_institution_financial_institution: {
        id: financialInstitutionId,
      },
    }
  );

  return {
    id,
    name: _.get(a, 'user_account_account.account_business_name'),
    role: {
      id: _.get(a, 'user_account_role.id'),
      name: _.get(a, 'user_account_role.user_role_role'),
    },
    financialInstitutionAccount: {
      banking: _.get(
        a,
        'user_account_account.account_financial_institution_accounts[0].financial_institution_account_for_banking'
      ),
      payments: _.get(
        a,
        'user_account_account.account_financial_institution_accounts[0].financial_institution_account_for_payments'
      ),
      lending: _.get(
        a,
        'user_account_account.account_financial_institution_accounts[0].financial_institution_account_for_lending'
      ),
      rate: _.get(
        a,
        'user_account_account.account_financial_institution_accounts[0].financial_institution_account_rate'
      ),
      bgCheckNeeded: _.get(
        a,
        'user_account_account.account_financial_institution_accounts[0].financial_institution_account_bg_check_needed'
      ),
      ndaNeeded: _.get(
        a,
        'user_account_account.account_financial_institution_accounts[0].financial_institution_account_nda_needed'
      ),
    },
    financialInstitution: {
      ...transformUserFI(
        _.get(
          financialInstitution,
          'user_financial_institution_financial_institution',
          {}
        )
      ),
      isNDASigned: _.get(
        financialInstitution,
        'user_financial_institution_nda_signed'
      ),
      isBgConsentSigned: _.get(
        financialInstitution,
        'user_financial_institution_bg_consent_signed'
      ),
      hasFinishedOnboarding: _.get(
        financialInstitution,
        'user_financial_institution_has_finished_onboarding'
      ),
      receiveExpirationNotification: _.get(
        financialInstitution,
        'user_financial_institution_receive_expiration_notifications'
      ),
      expirationNotificationFrequency: _.get(
        financialInstitution,
        'user_financial_institution_expiration_notification_frequency'
      ),
    },
  };
};
const transformComplianceAlertEmail = response => {
  if (!response) {
    return null;
  }
  return {
    isActive: response.compliance_alert_email_is_active,
    notifyFailed: response.compliance_alert_email_notify_failed,
    notifyPassed: response.compliance_alert_email_notify_passed,
    notifyReview: response.compliance_alert_email_notify_review,
    frequency: response.compliance_alert_email_frequency,
    id: response.id,
  };
};
const transformUser = (response, state) => {
  const accountRoles = {};

  const accounts = _.get(response, 'user_user_accounts', []).map(a =>
    transformUserAccount(a, response, accountRoles)
  );

  const selectedAccountId = response.selected_account_id;
  const emptyAccount = {
    role: {},
    financialInstitutionAccount: {},
    financialInstitution: transformUserFI(
      _.get(
        response,
        'user_user_financial_institutions[0].user_financial_institution_financial_institution',
        {}
      )
    ),
  };
  let selectedAccount = { ...emptyAccount };

  if (selectedAccountId) {
    selectedAccount = accounts.filter(a => a.id === selectedAccountId)[0];

    if (!selectedAccount && response.user_user_account != null) {
      selectedAccount = transformUserAccount(
        response.user_user_account,
        response,
        accountRoles
      );
    }
  } else if (accounts.length > 0) {
    selectedAccount = accounts[0];
  }

  const userRole =
    _.get(response, 'user_role') ||
    _.get(
      response,
      'user_user_financial_institutions[0].user_financial_institution_role'
    ) ||
    _.get(accountRoles, '[selectedAccount.id].user_account_role') ||
    {};
  const isWhiteLabel = window.location.host.split('.')[0].includes('localhost');
  const roleId = userRole.id;
  const role = userRole.user_role_role;
  const isSuperAdmin = userRole.is_super_admin;
  const isSolventAdmin = userRole.is_solvent_admin;
  // Solvent Admin has FI Primary Admin powers across ALL FIs
  const isPrimaryBankAdmin = userRole.is_fi_primary_admin || isSolventAdmin;
  const isBankAdmin = userRole.is_fi_admin || isPrimaryBankAdmin;
  const isBankReadOnly = userRole.is_fi_read_only;
  const isPrimaryMerchantAdmin = userRole.is_merchant_primary_admin;
  const isMerchantAdmin = userRole.is_merchant_admin || isPrimaryMerchantAdmin;
  const isMerchant = userRole.is_merchant_non_admin || isMerchantAdmin;

  if (isBankAdmin || isBankReadOnly) {
    selectedAccount = { ...emptyAccount };
  }

  const { financialInstitution } = selectedAccount;

  return {
    id: _.get(response, 'id'),
    email: _.get(response, 'user_email_address'),
    firstName: _.get(response, 'user_first_name'),
    lastName: _.get(response, 'user_last_name'),
    roleId,
    role,
    roleName: role,
    isSuperAdmin,
    isSolventAdmin,
    isPrimaryBankAdmin,
    isBankAdmin,
    isPrimaryMerchantAdmin,
    isBankReadOnly,
    isMerchantAdmin,
    isMerchant,
    isWhiteLabel,
    primaryNumber: response.user_primary_number,
    financialInstitutionId: financialInstitution.id,
    financialInstitutionName: financialInstitution.businessName,
    financialInstitutionLogo: financialInstitution.logo,
    financialInstitutionLogoBlue: financialInstitution.logoBlue,
    financialInstitutionLogoSmall: financialInstitution.logoSmall,
    financialInstitutionForBanking: financialInstitution.allowsBanking,
    financialInstitutionForPayments: financialInstitution.allowsPayments,
    financialInstitutionForLending: financialInstitution.allowsLending,
    financialInstitutionHasAssignAgents: financialInstitution.hasAssignAgents,
    financialInstitutionForInsurance: financialInstitution.allowsInsurance,
    financialInstitutionHasAudits: financialInstitution.hasAudits,
    financialInstitutionAccountForPayments:
      selectedAccount.financialInstitutionAccount.allowsPayments,
    financialInstitutionAccountForLending:
      selectedAccount.financialInstitutionAccount.allowsLending,
    financialInstitutionAccountForBanking:
      selectedAccount.financialInstitutionAccount.allowsBanking,
    financialInstitutionRate: selectedAccount.financialInstitutionAccount.rate,
    financialInstitutionBgCheckNeeded:
      selectedAccount.financialInstitutionAccount.bgCheckNeeded,
    financialInstitutionNdaNeeded:
      selectedAccount.financialInstitutionAccount.ndaNeeded,
    accountId: selectedAccount.id || state.data.fiSelectedAccountId,
    accountName: selectedAccount.name || state.data.fiSelectedAccountName,
    isNDASigned: financialInstitution.isNDASigned,
    isBgConsentSigned: financialInstitution.isBgConsentSigned,
    hasFinishedOnboarding: financialInstitution.hasFinishedOnboarding,
    complianceAlertEmail: !isSuperAdmin
      ? transformComplianceAlertEmail(
        response.user_user_financial_institutions[0]
          .user_financial_institution_compliance_alert_email
      )
      : null,
    notifyOnDocumentUpload: !isSuperAdmin
      ? response.user_user_financial_institutions[0]
        .user_financial_institution_notify_on_document_upload
      : null,
    receiveExpirationNotification: !isSuperAdmin
      ? response.user_user_financial_institutions[0]
        .user_financial_institution_receive_expiration_notifications || false
      : null,
    expirationNotificationFrequency: !isSuperAdmin
      ? response.user_user_financial_institutions[0]
        .user_financial_institution_expiration_notification_frequency || '30'
      : null,
    accounts,
    fiSelectedAccountId: state.data.fiSelectedAccountId,
    fiSelectedAccountName: state.data.fiSelectedAccountName,
  };
};

const userFetchReducer = createApiReducer(
  [
    FETCH_LOGGED_IN_USER_START,
    FETCH_LOGGED_IN_USER_SUCCESS,
    FETCH_LOGGED_IN_USER_FAILURE,
  ],
  transformUser,
  error =>
    `error while fetching user: ${_.get(
      error,
      'payload.response',
      'unknown error'
    )}`
);

const userImpersonateAccountReducer = createApiReducer(
  [null, IMPERSONATE_ACCOUNT, null],
  (payload, state) => {
    return {
      ...state.data,
      accountId: payload.accountId,
      accountName: payload.accountName,
      fiSelectedAccountId: payload.accountId,
      fiSelectedAccountName: payload.accountName,
    };
  },
  () => { }
);

const signOutReducer = (state, action) => {
  if (action.type === SIGN_OUT) {
    return {
      ...state,
      ...INITIAL_API_STATE,
      status: {
        state: Status.CLEAR,
        error: null,
        errorMessage: null,
      },
    };
  }
  return state;
};

const transformMerchantIds = (payload, state) => {
  const merchantIds = [];
  const allowedReports = [];
  payload.forEach(el =>
    el.mids
      ? merchantIds.push(el.mids)
      : allowedReports.push(...el.allowed_reports)
  );
  return {
    ...state.data,
    merchantIds,
    allowedReports,
  };
};

const fetchMerchantIdsReducer = createApiReducer(
  [FETCH_MIDS_START, FETCH_MIDS_SUCCESS, FETCH_MIDS_FAILURE],
  transformMerchantIds
);
const transformLoginHistory = response => {
  const logs = [];

  response.forEach(log => {
    const parser = new UAParser(log.user_agent_info);
    logs.push({
      ip: log.login_IP,
      email_address: log.login_user_email_address,
      status: log.status,
      details: log.user_login_activity_details,
      date: format(new Date(log.login_datetime), 'MM-DD-YYYY h:mm'),
      OS: parser.getOS().name,
      browser: parser.getBrowser().name,
    });
  });

  return {
    loginHistory: logs,
  };
};
const fetchLoginHistoryReducer = createApiReducer(
  [
    FETCH_LOGIN_HISTORY_START,
    FETCH_LOGIN_HISTORY_SUCCESS,
    FETCH_LOGIN_HISTORY_FAILURE,
  ],
  transformLoginHistory,
  error => _.get(error, 'payload.response', 'unknown error')
);
const loginhistory = chainReducers(INITIAL_API_STATE, fetchLoginHistoryReducer);
const user = chainReducers(
  INITIAL_API_STATE,
  userFetchReducer,
  userImpersonateAccountReducer,
  signOutReducer,
  fetchMerchantIdsReducer
);

export const reducer = combineReducers({
  user,
  loginhistory,
});
